import { useCallback } from 'react';

import { useUnsavedChangesStore } from '@/shared/store/unsavedChanges.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

type UnsavedChangesUiOptions = {
  onCancel?: () => void;
  onConfirm?: () => Promise<boolean>;
  onTrigger?: () => Promise<boolean>;
};

export type ConfirmUnsavedNavigationOptions = {
  shouldConfirm?: boolean;
  formId?: string;
  type: 'unsavedChanges';
  onCancel?: () => void;
  onConfirm?: () => Promise<boolean>;
  onTrigger?: () => Promise<boolean>;
};

export const useConfirmUnsavedNavigation = () => {
  const modals = useModalsStore();
  const getDirtyForm = useUnsavedChangesStore((state) => state.getDirtyForm);
  const hasUnsavedChanges = useUnsavedChangesStore((state) => state.hasUnsavedChanges);
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'closingConfirm' });

  const openUnsavedChangesConfirm = useCallback(
    ({ onCancel, onConfirm, onTrigger }: UnsavedChangesUiOptions) =>
      new Promise<boolean>((resolve) => {
        modals.openModal({
          type: 'ConfirmModal',
          variant: 'confirm',
          title: t('title'),
          bodyText: t.rich('areYouSure'),
          cancelText: t('cancel'),
          confirmText: t('confirm'),
          onConfirm: async () => {
            try {
              const isValid = (await onTrigger?.()) ?? true;
              if (!isValid) {
                resolve(false);
                return;
              }

              resolve((await onConfirm?.()) ?? true);
            } catch {
              resolve(false);
            }
          },
          onCancel: () => {
            onCancel?.();
            resolve(false);
          },
        });
      }),
    [modals, t],
  );

  return useCallback(
    async (options?: ConfirmUnsavedNavigationOptions) => {
      const formGuard = getDirtyForm(options?.formId);
      const hasFormId = Boolean(formGuard?.formId);

      const resolvedShouldConfirm =
        options?.shouldConfirm ?? (hasFormId ? formGuard !== null : hasUnsavedChanges());

      if (!resolvedShouldConfirm) return true;

      const resolvedOnConfirm =
        options?.onConfirm ??
        (formGuard?.callbacks.onConfirm
          ? async () => {
              await formGuard.callbacks.onConfirm?.();
              return true;
            }
          : undefined);
      const resolvedOnCancel =
        options?.onCancel ?? (hasFormId ? formGuard?.callbacks.onCancel : undefined);
      const resolvedOnTrigger = options?.onTrigger ?? formGuard?.callbacks.onTrigger;

      return await openUnsavedChangesConfirm({
        onCancel: resolvedOnCancel,
        onConfirm: resolvedOnConfirm,
        onTrigger: resolvedOnTrigger,
      });
    },
    [getDirtyForm, hasUnsavedChanges, openUnsavedChangesConfirm],
  );
};
